import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import BillingService from '@/services/BillingService';
import {
  ArrowRight,
  Banknote,
  BarChart3,
  Brain,
  Check,
  FileText,
  Menu,
  Shield,
  Star,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';

const LandingPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">
                BetterCapital.us
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900">
                Features
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">
                Pricing
              </a>
              <a
                href="#testimonials"
                className="text-gray-600 hover:text-gray-900"
              >
                Testimonials
              </a>
              <a href="/about" className="text-gray-600 hover:text-gray-900">
                About
              </a>
              <Button
                variant="outline"
                className="mr-2"
                onClick={() => {
                  window.location.href = '/app';
                }}
              >
                Sign In
              </Button>
              <Button
                onClick={() => {
                  window.location.href = '/app';
                }}
              >
                Get Started
              </Button>
            </div>
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
                <a
                  href="#features"
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  Pricing
                </a>
                <a
                  href="#testimonials"
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  Testimonials
                </a>
                <a
                  href="/about"
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  About
                </a>
                <div className="px-3 py-2 space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      window.location.href = '/app';
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    className="w-full"
                    onClick={() => {
                      window.location.href = '/app';
                    }}
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="secondary" className="mb-4 px-4 py-2">
              <Brain className="w-4 h-4 mr-2" />
              AI-Powered Real Estate Investment Platform
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Unlock the True Potential of Your
              <span className="text-blue-600"> Real Estate Investments</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Maximize your capital returns with our AI-driven platform that
              provides intelligent analytics, automated tracking, and actionable
              insights for your real estate portfolio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="text-lg px-8 py-6"
                onClick={() => {
                  window.location.href = '/app';
                }}
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="text-lg px-8 py-6"
                onClick={() =>
                  window.open('https://calendly.com/bayflip/30min-1', '_blank')
                }
              >
                Schedule Demo
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Optimize Your Real Estate Portfolio
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive platform combines AI-powered analytics with
              traditional real estate investment tools to help you make smarter
              decisions and maximize returns.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* AI Analytics */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle>AI-Powered Analytics</CardTitle>
                <CardDescription>
                  Get intelligent insights and recommendations powered by
                  advanced machine learning algorithms.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Portfolio performance analysis
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Predictive cash flow modeling
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Market trend identification
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* KPI Tracking */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Advanced KPI Tracking</CardTitle>
                <CardDescription>
                  Monitor 15+ key performance indicators including cap rates,
                  ROI, and cash-on-cash returns.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Real-time performance metrics
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Comparative property analysis
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Custom KPI dashboards
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Bank Integration */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <Banknote className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Automated Bank Sync</CardTitle>
                <CardDescription>
                  Connect multiple bank accounts for automatic transaction
                  categorization and tracking.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Multi-bank account support
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Automatic transaction categorization
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Real-time financial updates
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Document Management */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>Smart Document Management</CardTitle>
                <CardDescription>
                  Organize and store all your property documents with AI-powered
                  categorization.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Google Drive integration
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Email-based document upload
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Automatic document tagging
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Team Collaboration */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-indigo-600" />
                </div>
                <CardTitle>Team Collaboration</CardTitle>
                <CardDescription>
                  Share properties with team members and assign role-based
                  access controls.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Role-based permissions
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Multi-user property access
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Real-time collaboration
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Security & Compliance */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle>Bank-Grade Security</CardTitle>
                <CardDescription>
                  Your financial data is protected with enterprise-level
                  security and compliance.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    End-to-end encryption
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    Regular security audits
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start free and scale as your portfolio grows. All plans include
              our core AI analytics and KPI tracking.
            </p>
          </div>

          {(() => {
            const [billingInterval, setBillingInterval] = useState<
              'monthly' | 'annual'
            >('monthly');

            const handleSubscribe = async (
              plan: 'starter' | 'pro' | 'premium',
            ) => {
              try {
                const result = await BillingService.createCheckoutSession(
                  plan,
                  billingInterval,
                );

                if (result.success) {
                  if (result.url) {
                    // Redirect to Stripe checkout
                    window.location.href = result.url;
                  } else if (result.redirectUrl) {
                    // Redirect to success page for free plan
                    window.location.href = result.redirectUrl;
                  }
                } else {
                  toast.error(
                    result.message || 'Failed to create checkout session',
                  );
                }
              } catch (error) {
                console.error('Error creating checkout session:', error);
                toast.error('Failed to start subscription process');
              }
            };

            const getPrice = (plan: 'starter' | 'pro' | 'premium') => {
              if (plan === 'starter') return '$0';
              if (plan === 'pro')
                return billingInterval === 'annual' ? '$299' : '$29.95';
              if (plan === 'premium')
                return billingInterval === 'annual' ? '$899' : '$89.95';
              return '$0';
            };

            const getSavings = (plan: 'pro' | 'premium') => {
              if (billingInterval === 'annual') {
                return plan === 'pro' ? '17% savings' : '17% savings';
              }
              return '';
            };

            return (
              <>
                {/* Billing Toggle */}
                <div className="flex justify-center mb-8">
                  <div className="bg-gray-100 rounded-lg p-1 flex">
                    <button
                      type="button"
                      onClick={() => setBillingInterval('monthly')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        billingInterval === 'monthly'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingInterval('annual')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        billingInterval === 'annual'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Annual
                      <span className="ml-1 text-xs text-green-600 font-semibold">
                        (Save 17%)
                      </span>
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                  {/* Starter Plan */}
                  <Card className="border-2 border-gray-200">
                    <CardHeader className="text-center">
                      <CardTitle className="text-2xl">Starter</CardTitle>
                      <CardDescription>
                        Perfect for getting started
                      </CardDescription>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">
                          {getPrice('starter')}
                        </span>
                        <span className="text-gray-500">
                          /{billingInterval === 'annual' ? 'year' : 'month'}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Up to 5 assets
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />2
                          years transaction history
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          1GB document storage
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Manual bank sync
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Basic dashboard
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Community support
                        </li>
                      </ul>
                      <Button
                        className="w-full"
                        variant="outline"
                        onClick={() => handleSubscribe('starter')}
                      >
                        Get Started Free
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Pro Plan */}
                  <Card className="border-2 border-blue-500 relative">
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-500 text-white px-4 py-1">
                        Most Popular
                      </Badge>
                    </div>
                    <CardHeader className="text-center">
                      <CardTitle className="text-2xl">Pro</CardTitle>
                      <CardDescription>For growing portfolios</CardDescription>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">
                          {getPrice('pro')}
                        </span>
                        <span className="text-gray-500">
                          /{billingInterval === 'annual' ? 'year' : 'month'}
                        </span>
                        {getSavings('pro') && (
                          <div className="text-sm text-green-600 font-medium">
                            {getSavings('pro')}
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Up to 25 assets
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />7
                          years transaction history
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          10GB document storage
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Auto-weekly bank sync
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Up to 10 banks
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          12 owner statements import
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Google Drive integration
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Standard analytics
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />1
                          free viewer
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Email support
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          20% off FlipSystem & MTK
                        </li>
                      </ul>
                      <Button
                        className="w-full"
                        onClick={() => handleSubscribe('pro')}
                      >
                        Start Pro Trial
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Premium Plan */}
                  <Card className="border-2 border-gray-200">
                    <CardHeader className="text-center">
                      <CardTitle className="text-2xl">Premium</CardTitle>
                      <CardDescription>For serious investors</CardDescription>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">
                          {getPrice('premium')}
                        </span>
                        <span className="text-gray-500">
                          /{billingInterval === 'annual' ? 'year' : 'month'}
                        </span>
                        {getSavings('premium') && (
                          <div className="text-sm text-green-600 font-medium">
                            {getSavings('premium')}
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Up to 100 assets
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />7
                          years transaction history
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          100GB document storage
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Auto-weekly bank sync
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Up to 10 banks
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Unlimited owner statements
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Google Drive integration
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Advanced analytics
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />3
                          free viewers
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Priority support
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Concierge onboarding
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Exclusive FlipSystem access
                        </li>
                      </ul>
                      <Button
                        className="w-full"
                        variant="outline"
                        onClick={() => handleSubscribe('premium')}
                      >
                        Start Premium Trial
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Real Estate Investors
            </h2>
            <p className="text-xl text-gray-600">
              See how BetterCapital is helping investors maximize their returns
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600 mb-4">
                  "BetterCapital's AI analytics helped me identify
                  underperforming properties and optimize my portfolio. My ROI
                  increased by 23% in just 6 months."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-semibold">SM</span>
                  </div>
                  <div>
                    <p className="font-semibold">Sarah Martinez</p>
                    <p className="text-sm text-gray-500">
                      Portfolio Manager, 15 properties
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600 mb-4">
                  "The automated bank sync and KPI tracking saves me hours every
                  week. I can focus on growing my business instead of managing
                  spreadsheets."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-green-600 font-semibold">DJ</span>
                  </div>
                  <div>
                    <p className="font-semibold">David Johnson</p>
                    <p className="text-sm text-gray-500">
                      Real Estate Investor, 8 properties
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600 mb-4">
                  "The team collaboration features are game-changing. My
                  property manager and I can work together seamlessly, and the
                  role-based access keeps everything secure."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-purple-600 font-semibold">LC</span>
                  </div>
                  <div>
                    <p className="font-semibold">Lisa Chen</p>
                    <p className="text-sm text-gray-500">
                      Property Owner, 12 properties
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Maximize Your Real Estate Returns?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join thousands of investors who are already using BetterCapital to
            optimize their portfolios and increase their capital returns.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              variant="secondary"
              className="text-lg px-8 py-6"
              onClick={() => {
                window.location.href = '/app';
              }}
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="secondary"
              className="text-lg px-8 py-6"
              onClick={() =>
                window.open('https://calendly.com/bayflip/30min-1', '_blank')
              }
            >
              Schedule a Demo
            </Button>
          </div>
          <p className="text-sm text-blue-200 mt-4">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">BetterCapital.us</span>
              </div>
              <p className="text-gray-400">
                The AI-powered platform for real estate investors who want to
                maximize their returns.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#features" className="hover:text-white">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white">
                    Pricing
                  </a>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    API
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Integrations
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="/about" className="hover:text-white">
                    About
                  </a>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Blog
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Careers
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Help Center
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Documentation
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Status
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Security
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 BetterCapital.us. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
